/**
 * Patch the inbox conversation list from a poll payload.
 * Known sessions get preview/time/unread updates and move to the top.
 * Unknown session ids are reported so the caller can refresh the full list.
 */

import { getInboxSelectedConversationId } from "./inboxAlertSelection.js";

/**
 * @param {number | string | null | undefined} createdAt
 * @returns {string}
 */
export function formatPollListTimestamp(createdAt) {
  const n = Number(createdAt);
  const d = Number.isFinite(n) && n > 0 ? new Date(n < 1e12 ? n * 1000 : n) : new Date();
  if (Number.isNaN(d.getTime())) {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * @param {Array<Record<string, unknown>>} prev
 * @param {{
 *   messages?: Array<{ session_id?: string, message_content?: string, created_at?: number }>,
 *   handoffs?: Array<{ session_id?: string, created_at?: number }>,
 * }} payload
 * @param {{ openConversationId?: string | null }} [opts]
 * @returns {{ next: Array<Record<string, unknown>>, unknownSessionIds: string[] }}
 */
export function applyInboxPollEventsToList(prev, payload, opts = {}) {
  const list = Array.isArray(prev) ? [...prev] : [];
  const openId = String(
    opts.openConversationId ?? getInboxSelectedConversationId() ?? "",
  );
  const unknown = new Set();

  const bump = (sessionId, { preview, createdAt, incrementUnread }) => {
    const sid = String(sessionId || "").trim();
    if (!sid) return;
    const idx = list.findIndex((c) => String(c.id) === sid);
    if (idx < 0) {
      unknown.add(sid);
      return;
    }
    const row = { ...list[idx] };
    if (preview != null && String(preview).trim()) {
      row.preview = String(preview);
    }
    if (createdAt != null) {
      row.timestamp = formatPollListTimestamp(createdAt);
    }
    if (incrementUnread && sid !== openId) {
      const current = typeof row.unread === "number" ? row.unread : 0;
      row.unread = current + 1;
    }
    list.splice(idx, 1);
    list.unshift(row);
  };

  for (const msg of payload?.messages ?? []) {
    bump(msg.session_id, {
      preview: msg.message_content,
      createdAt: msg.created_at,
      incrementUnread: true,
    });
  }
  for (const hf of payload?.handoffs ?? []) {
    bump(hf.session_id, {
      preview: undefined,
      createdAt: hf.created_at,
      incrementUnread: false,
    });
  }

  return { next: list, unknownSessionIds: [...unknown] };
}
